import { useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import Items from '@aytm/app/stats/page/question-views/hm/components/Items'
import Controls from '@aytm/app/stats/page/question-views/hm/components/controls/Controls'
import Chart from '@aytm/app/stats/page/question-views/hm/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import { calcImageScale } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/utils'
import { getModelValue, getColorTheme } from '@aytm/app/stats/lib/stat-page'
import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import { AnswersStats } from '@aytm/app/stats/page/question-views/hm/components/answers-stats/AnswersStats'

// internal
import { useControlsForm } from '@aytm/app/stats/page/question-views/hm/lib/hooks'
import {
  calcTableData,
  buildOnCommentsClick,
  buildOnChartCommentsClick,
  calcVariantCommentsScope
} from '@aytm/app/stats/page/question-views/hm/lib/table-utils'
import { applySorted, calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { calcAnswerVariantItems } from '@aytm/app/stats/page/question-views/hm/lib/props/variants'
import useHeatmapState from '@aytm/app/stats/page/question-views/hm/states/useHeatmapState'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

// styles
import style from '@aytm/app/stats/page/question-views/hm/styles/Block.module.scss'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// First paint, before the report column is read.
const DEFAULT_WIDTH = 814

// Everything the researcher sees once the numbers are crunched: the controls, the image with the
// pins on it, the statistics of what was pinned, the table, and the expandable list where every pin
// group or cluster draws the verbatims behind it. What only this half needs is derived here, so a
// question with nothing to draw never pays for it.
const BlockBody = ({ state, apiRef, ...props }) => {
  const {
    id,
    questionKey,
    questionId,
    userRoles,
    answer,
    components,
    batches,
    visualPosition,
    onFetchCRUDKey,
    onUpdateData,
    image,
    qLogicVisibility,
    features,
    link,
    isReportXpert,
    globalChartMode
  } = props

  const {
    viewMode,
    clustersMode,
    statType,
    blockType,
    stopWordLevel,
    config,
    clusters,
    loading,
    sortOrder,
    onModelChange,
    onSortChange
  } = state

  const answerIsLoaded = answer !== 'loading'

  const { form, editClusterId, changeClustersForm, currentCluster, tip, setTip, getLassoData, setLassoData } =
    useControlsForm({
      clusters,
      components,
      viewMode,
      answer,
      config,
      batches,
      qLogicVisibility,
      loading,
      clustersMode,
      statType,
      blockType,
      stopWordLevel
    })

  // the image is drawn at whatever the block is wide, so the frame held open while the answer loads
  // and until the map is scrolled to is the image's own height under the same scale; a question with
  // no image has none
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const chartHeight = useMemo(() => {
    const { width: imageWidth, height: imageHeight } = image?.original || {}
    if (!imageHeight) return null

    return Math.round(imageHeight * calcImageScale(width, imageWidth))
  }, [image, width])

  // chartMode map/none renders tables only -- controls and chart stay hidden
  const chartOn = !isNoneChartMode(props.chartMode, globalChartMode)

  const onCommentsClick = buildOnCommentsClick({
    id,
    questionId,
    link,
    isReportXpert,
    statType,
    batches,
    clusters,
    config
  })
  const tableData = calcTableData({
    statType,
    answer,
    batches,
    clusters,
    features,
    reportPercentDecimals: props.reportPercentDecimals ?? getModelValue('percentDecimals'),
    onCommentsClick
  })

  // a header pick names the order it wants; the state settles on it and the shell writes it down
  const onSort = useCallback(
    (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) onSortChange(order)
    },
    [onSortChange]
  )

  return (
    <>
      {chartOn && isComponentOn(components, 'options_chart') && (
        <div className={style.controls}>
          <Controls
            id={id}
            components={components}
            userRoles={userRoles}
            clusters={clusters}
            batches={batches}
            config={config}
            visualPosition={visualPosition}
            image={image}
            form={form}
            tip={tip}
            getLassoData={getLassoData}
            currentCluster={currentCluster}
            changeClustersForm={changeClustersForm}
            onFetchCRUDKey={onFetchCRUDKey}
            onUpdateData={onUpdateData}
            onChangeModel={onModelChange}
          />
        </div>
      )}
      {chartOn && isComponentOn(components, 'chart') && (
        <ChartPlaceholder height={chartHeight} isLoading={!answerIsLoaded}>
          <Chart
            apiRef={apiRef}
            id={id}
            questionKey={questionKey}
            image={image}
            answer={answer}
            loading={form.isLoading}
            viewMode={viewMode}
            clustersMode={clustersMode}
            clusterFormMode={form.clustersForm.mode}
            clusters={clusters}
            batches={batches}
            config={config}
            activeClusterId={editClusterId}
            userRoles={userRoles}
            lassoCluster={currentCluster}
            onTipChange={setTip}
            onLassoChange={setLassoData}
            onCommentsClick={buildOnChartCommentsClick({
              id,
              questionId,
              link,
              isReportXpert,
              batches,
              clusters,
              config
            })}
            changeClustersForm={changeClustersForm}
          />
        </ChartPlaceholder>
      )}
      {isComponentOn(components, 'options_answers') && answerIsLoaded && (
        <AnswersStats form={form} isLoading={form.isLoading} onChangeModel={onModelChange} />
      )}

      {isComponentOn(components, 'table') && answerIsLoaded && (
        <Table cols={applySorted(tableData.cols, sortOrder)} rows={tableData.rows} onSort={onSort} />
      )}
    </>
  )
}

BlockBody.displayName = 'HeatmapBlockBody'

// The expandable list closing the block: every pin group or cluster drawing the verbatims behind it.
// It is the block's own half rather than the body's, so it can be closed off in a section of its own
// -- what it needs is derived here and the body pays for none of it.
const AnswerVariantItems = ({ state, ...props }) => {
  const { id, questionKey, answer, components, batches, isReportXpert, handlers = {} } = props
  const { statType, config, clusters, uncollapsed, onToggle, onToggleAll } = state

  const items = useMemo(
    () => calcAnswerVariantItems({ statType, batches, clusters, config }),
    [statType, batches, clusters, config]
  )

  const commentsScopeOf = useMemo(
    () => item => calcVariantCommentsScope({ statType, item, clusters, config }),
    [statType, clusters, config]
  )

  const variantHandlers = useMemo(
    () => ({
      ...handlers,
      onAnswerVariantReady: state.onAnswerVariantReady,
      onAnswerVariantStateChange: state.onAnswerVariantStateChange,
      onAnswerVariantWcParamsChange: state.onAnswerVariantWcParams
    }),
    [handlers, state.onAnswerVariantReady, state.onAnswerVariantStateChange, state.onAnswerVariantWcParams]
  )

  return (
    <Items
      items={items}
      statType={statType}
      uncollapsed={uncollapsed}
      questionId={id}
      questionKey={questionKey}
      type={props.type}
      format={props.format}
      components={components}
      answer={answer}
      // a row is a question of its own, but it hides or draws its chart by the mode the heatmap declares
      questionChartMode={props.chartMode}
      isReportXpert={isReportXpert}
      answerVariantParams={props.answerVariantParams}
      loadingRows={state.loadingRows}
      reportPercentDecimals={props.reportPercentDecimals ?? getModelValue('percentDecimals')}
      commentsScopeOf={commentsScopeOf}
      handlers={variantHandlers}
      onToggle={onToggle}
      onToggleAll={collapse =>
        onToggleAll(
          collapse,
          items.map(item => item.position)
        )
      }
    />
  )
}

AnswerVariantItems.displayName = 'HeatmapAnswerVariantItems'

// The heatmap block: the image with the pins on it, the controls above it, the statistics of what
// was pinned, and the expandable list where every pin group or cluster draws the verbatims behind
// it. A question whose numbers have not been crunched yet shows the run band instead.
//
// block.coffee owns the mount, the report-param write and the fetch. Everything the researcher
// sees and moves is here, and the block answers the exporters for its whole subtree through the
// api it hands up.
const Block = props => {
  const { questionKey, answer, visualPosition, onChartReady } = props

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useHeatmapState({
    params: props.params,
    paramsRevision: props.paramsRevision,
    questionKey,
    visualPosition,
    answer,
    onReady: props.onReady,
    onStateChange: props.onStateChange,
    onUpdateData: props.onUpdateData,
    onAnswerVariantParamsChange: props.onAnswerVariantParamsChange,
    onAnswerVariantWcParamsChange: props.onAnswerVariantWcParamsChange,
    onAnswerVariantReady: props.onAnswerVariantReady
  })

  // Live-page PPTX bridge: ActionButtons fills this in (it owns the svg container and the base64
  // image); the shell holds onto it for the download path.
  const apiRef = useRef({})

  useEffect(() => {
    onChartReady?.(apiRef.current)
  }, [onChartReady])

  // the question's own rule, applied to the payload on screen -- read here rather than taken as
  // a flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(props.calcBadData?.(answer))

  // the variants list closes the block, so the response count sits under it rather than under the
  // table; a block drawing no list carries the count itself
  const itemsOn = 'loading' !== answer && !badData && isComponentOn(props.components, 'fields')

  return (
    <>
      <BlockWrapper {...props}>
        <DataRun
          answer={answer}
          badData={badData}
          type={props.type}
          questionKey={questionKey}
          schemaKey={props.schemaKey}
          setUp={Boolean(props.image?.original)}
          missing="image"
          colorTheme={getColorTheme()}
          calcBadData={props.calcBadData}
          onBadData={props.onBadData}
          onComplete={props.onRunComplete}
        />
        {/* while the numbers have not been crunched there is nothing else to show */}
        {badData ? null : <BlockBody {...props} state={state} apiRef={apiRef} />}
      </BlockWrapper>

      {itemsOn && (
        <BlockWrapper answer={answer}>
          <AnswerVariantItems {...props} state={state} />
        </BlockWrapper>
      )}
    </>
  )
}

Block.displayName = 'HeatmapBlock'

// What the shell hands in: the question, the sections of the report params the block owns, the
// params each open row owns, and the handlers the surfaces report back through.
export const BLOCK_PROP_TYPES = {
  id: PropTypes.string.isRequired,
  questionId: PropTypes.string,
  questionKey: PropTypes.string,
  type: PropTypes.string,
  schemaKey: PropTypes.string,
  userRoles: PropTypes.array,
  answer: answerPropType,
  format: PropTypes.object,
  components: PropTypes.object,
  batches: PropTypes.array,
  visualPosition: PropTypes.number,
  onFetchCRUDKey: PropTypes.func,
  image: PropTypes.object,
  qLogicVisibility: PropTypes.string,
  features: PropTypes.object,
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  chartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  params: PropTypes.shape({
    hm: PropTypes.object,
    s: PropTypes.string,
    cb: PropTypes.string
  }),
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  // the report params every row owns, keyed by position
  answerVariantParams: PropTypes.object,
  handlers: PropTypes.object,
  calcBadData: PropTypes.func,
  onBadData: PropTypes.func,
  onRunComplete: PropTypes.func,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onUpdateData: PropTypes.func,
  onAnswerVariantReady: PropTypes.func,
  onAnswerVariantParamsChange: PropTypes.func,
  onAnswerVariantWcParamsChange: PropTypes.func,
  onChartReady: PropTypes.func
}

Block.propTypes = BLOCK_PROP_TYPES

// the body is handed the block's own props, plus the state and the pptx bridge the block owns
BlockBody.propTypes = { ...BLOCK_PROP_TYPES, state: PropTypes.object, apiRef: PropTypes.object }

export default Block
